import { useState } from "react";
import { Plus, X, History } from "lucide-react";
import { OPTION_CATEGORIES } from "@/config/modules";
import { useAuth } from "@/context/AuthContext";
import { api, errMsg } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { PreferencesCard } from "@/components/PreferencesCard";
import { DropdownLockCard } from "@/components/DropdownLockCard";
import { UlidSettingsCard } from "@/components/UlidSettingsCard";

const pretty = (s) => s.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export default function Settings() {
  const { options, loadOptions } = useAuth();
  const [drafts, setDrafts] = useState({});
  const [filter, setFilter] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [lock, setLock] = useState({ can_edit: false, unlocked: false });
  const canEdit = lock.can_edit && lock.unlocked;

  const loadHistory = async () => {
    try {
      const { data } = await api.get("/audit/options", { params: { limit: 100 } });
      setHistory(data);
      setShowHistory(true);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const add = async (cat) => {
    const val = (drafts[cat] || "").trim();
    if (!val) return;
    try {
      await api.post("/options", { category: cat, value: val });
      setDrafts({ ...drafts, [cat]: "" });
      await loadOptions();
      if (showHistory) loadHistory();
      toast.success(`Added "${val}"`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const remove = async (cat, value) => {
    try {
      await api.delete("/options", { params: { category: cat, value } });
      await loadOptions();
      if (showHistory) loadHistory();
      toast.success(`Removed "${value}"`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const cats = [...new Set([...OPTION_CATEGORIES, ...Object.keys(options)])]
    .filter((c) => pretty(c).toLowerCase().includes(filter.toLowerCase()));

  return (
    <div data-testid="settings-page">
      <h1 className="font-heading text-2xl sm:text-3xl font-extrabold tracking-tight text-[#111827]">Dropdown Settings</h1>
      <p className="text-sm text-[#6B7280] mt-1">
        Every dropdown in LabTrack is editable here by administrators, after confirming an emailed code.
        Other staff can request a new value from any dropdown while filling a form.
      </p>

      <PreferencesCard />

      <div className="mt-4">
        <UlidSettingsCard />
      </div>

      <div className="mt-4">
        <DropdownLockCard onUnlockChange={setLock} onApproved={loadOptions} />
      </div>

      <div className="flex flex-wrap items-center gap-3 mt-6">
        <Input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter dropdown lists…"
          data-testid="settings-filter" className="max-w-sm bg-white" />
        <Button variant="outline" onClick={() => (showHistory ? setShowHistory(false) : loadHistory())}
          data-testid="option-history-btn">
          <History className="h-4 w-4 mr-2 text-[#002FA7]" /> {showHistory ? "Hide change history" : "Change history"}
        </Button>
      </div>

      {showHistory && (
        <div className="mt-4 rounded-md border border-[#E5E7EB] bg-white" data-testid="option-history-panel">
          <div className="px-4 py-3 border-b border-[#E5E7EB]">
            <h2 className="font-heading font-bold text-sm text-[#111827]">Master data change history</h2>
            <p className="text-xs text-[#6B7280] mt-0.5">Who added or removed each dropdown value, most recent first.</p>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-[#E5E7EB]">
            {history.length === 0 && <p className="p-4 text-sm text-[#6B7280]" data-testid="option-history-empty">No changes recorded yet.</p>}
            {history.map((h) => (
              <div key={h.audit_id} className="px-4 py-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm"
                data-testid={`option-history-${h.audit_id}`}>
                <span className={`text-[10px] uppercase tracking-[0.1em] px-2 py-0.5 rounded-full border ${
                  h.action === "option_added" ? "text-[#2E7D32] border-[#2E7D32]" : "text-[#C92A2A] border-[#C92A2A]"}`}>
                  {h.action === "option_added" ? "added" : "removed"}
                </span>
                <span className="text-[#111827] font-medium">{h.value}</span>
                <span className="text-xs text-[#6B7280]">in {pretty(h.category)}</span>
                <span className="text-xs text-[#6B7280] ml-auto">
                  {h.user} · {String(h.at).slice(0, 16).replace("T", " ")}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 xl:grid-cols-3 gap-4 mt-4">
        {cats.map((cat) => (
          <div key={cat} className="rounded-md border border-[#E5E7EB] bg-white p-4" data-testid={`option-group-${cat}`}>
            <h2 className="font-heading font-semibold text-sm text-[#111827]">{pretty(cat)}</h2>
            <div className="flex flex-wrap gap-1.5 mt-3 min-h-[28px]">
              {(options[cat] || []).map((v) => (
                <span key={v} data-testid={`option-chip-${cat}-${v}`}
                  className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] border border-[#E5E7EB] px-2.5 py-1 text-xs text-[#111827]">
                  {v}
                  {canEdit && (
                    <button onClick={() => remove(cat, v)} data-testid={`remove-option-${cat}-${v}`}
                      className="text-[#6B7280] hover:text-[#C92A2A]"><X className="h-3 w-3" /></button>
                  )}
                </span>
              ))}
              {(options[cat] || []).length === 0 && <span className="text-xs text-[#6B7280]">No values yet</span>}
            </div>
            {canEdit && (
              <div className="flex gap-1.5 mt-3">
                <Input value={drafts[cat] || ""} onChange={(e) => setDrafts({ ...drafts, [cat]: e.target.value })}
                  onKeyDown={(e) => e.key === "Enter" && add(cat)} placeholder="Add value"
                  data-testid={`add-option-input-${cat}`} className="h-9 text-sm" />
                <Button size="sm" onClick={() => add(cat)} data-testid={`add-option-btn-${cat}`}
                  className="bg-[#002FA7] hover:bg-[#00237C] text-white h-9"><Plus className="h-4 w-4" /></Button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
